import {
  getBezierPath,
  EdgeProps,
  BaseEdge,
  EdgeToolbar,
  EdgeLabelRenderer,
} from '@xyflow/react';
import {
  ArrowDownToLine,
  IterationCw,
  RotateCcw,
  Scale,
  Trash2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useMemo } from 'react';
import { useEdgeInteraction } from './use-edge-interaction';
import {
  REPEAT_BACK_EDGE_ROLE,
  REPEAT_CONDITION_ROLE,
} from '@/pages/workflow/helper/repeat-control';
import './style.css';

export default function CustomEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  markerEnd,
  selected,
}: EdgeProps) {
  const {
    strokeColor,
    showDeleteToolbar,
    handleEdgeClick,
    handleDeleteClick,
    isConditional,
    branchLabel,
    isSelectedBranch,
    hasSelectedBranch,
    repeatRole,
    forEachRole,
  } = useEdgeInteraction(id, selected);
  const isRepeatBackEdge = repeatRole === REPEAT_BACK_EDGE_ROLE;
  const isRepeatConditionEdge = repeatRole === REPEAT_CONDITION_ROLE;
  const isCollectEdge = forEachRole === 'collect';
  const isEachItemEdge = forEachRole === 'item';

  const [edgePath, centerX, centerY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  const edgeStyle = useMemo(
    () => ({
      ...style,
      stroke: strokeColor,
      // Persisted edges can carry `style: null` (backend serializes the field
      // explicitly), and React Flow forwards it verbatim — the `style = {}`
      // parameter default only applies to `undefined`, so guard the read.
      strokeWidth:
        isRepeatBackEdge ||
        isRepeatConditionEdge ||
        isCollectEdge ||
        isEachItemEdge
          ? 2.5
          : style?.strokeWidth,
      // --- repeat loop (Phase 3): the back edge is the dashed one ---
      // --- for each (Phase 8c): so is the collector ---
      ...(isRepeatBackEdge || isCollectEdge ? { strokeDasharray: '8 6' } : {}),
      ...(isConditional
        ? {
            strokeWidth: isSelectedBranch ? 3 : 2,
            strokeDasharray: '7 5',
            opacity: hasSelectedBranch && !isSelectedBranch ? 0.38 : 1,
          }
        : {}),
    }),
    [
      style,
      strokeColor,
      isRepeatBackEdge,
      isRepeatConditionEdge,
      isCollectEdge,
      isEachItemEdge,
      isConditional,
      isSelectedBranch,
      hasSelectedBranch,
    ],
  );

  return (
    <>
      {/* Invisible path with larger stroke width for easier selection */}
      <path
        d={edgePath}
        fill='none'
        stroke='transparent'
        strokeWidth={30}
        className='react-flow__edge-path edge-hitbox'
        onClick={handleEdgeClick}
        style={{ cursor: 'pointer', stroke: 'transparent' }}
      />
      {/* Visible edge path */}
      <BaseEdge
        id={id}
        path={edgePath}
        style={edgeStyle}
        markerEnd={markerEnd}
        className={`react-flow__edge-path ${selected ? 'edge-path-selected' : ''}`}
        pointerEvents='none'
      />
      {isConditional && branchLabel && (
        <EdgeLabelRenderer>
          <div
            className={`conditional-edge-label ${
              isSelectedBranch ? 'conditional-edge-label-selected' : ''
            }`}
            style={{
              transform: `translate(-50%, -50%) translate(${centerX}px, ${centerY}px)`,
              opacity: hasSelectedBranch && !isSelectedBranch ? 0.5 : 1,
            }}
            data-testid={`conditional-edge-label-${branchLabel}`}
            data-taken={
              hasSelectedBranch
                ? isSelectedBranch
                  ? 'true'
                  : 'false'
                : undefined
            }
          >
            {branchLabel}
            {/* --- explain every decision (Phase 4): untaken branches say so --- */}
            {hasSelectedBranch && !isSelectedBranch && (
              <span
                className='conditional-edge-not-taken'
                data-testid='conditional-edge-not-taken'
              >
                not taken
              </span>
            )}
          </div>
        </EdgeLabelRenderer>
      )}
      {(isRepeatBackEdge || isRepeatConditionEdge) && (
        <EdgeLabelRenderer>
          <div
            className={`pointer-events-none absolute flex items-center gap-1 rounded-full border bg-background px-2 py-1 text-[10px] font-semibold shadow-sm ${
              isRepeatBackEdge
                ? 'border-amber-400 text-amber-800 dark:text-amber-200'
                : 'border-violet-300 dark:border-violet-500/60 text-violet-800 dark:text-violet-200'
            }`}
            style={{
              transform: `translate(-50%, -50%) translate(${centerX}px, ${centerY}px)`,
            }}
          >
            {isRepeatBackEdge ? (
              <RotateCcw className='size-3' />
            ) : (
              <Scale className='size-3' />
            )}
            {isRepeatBackEdge ? 'repeat' : 'judge'}
          </div>
        </EdgeLabelRenderer>
      )}
      {(isCollectEdge || isEachItemEdge) && (
        <EdgeLabelRenderer>
          <div
            className='pointer-events-none absolute flex items-center gap-1 rounded-full border border-teal-300 dark:border-teal-500/60 bg-background px-2 py-1 text-[10px] font-semibold text-teal-800 dark:text-teal-200 shadow-sm'
            style={{
              transform: `translate(-50%, -50%) translate(${centerX}px, ${centerY}px)`,
            }}
            data-testid={
              isCollectEdge ? 'for-each-collect-label' : 'for-each-item-label'
            }
          >
            {isCollectEdge ? (
              <ArrowDownToLine className='size-3' />
            ) : (
              <IterationCw className='size-3' />
            )}
            {isCollectEdge ? 'collect' : 'each item'}
          </div>
        </EdgeLabelRenderer>
      )}
      {showDeleteToolbar && (
        <EdgeToolbar edgeId={id} x={centerX} y={centerY}>
          <div className='edge-toolbar-wrapper'>
            <Button
              variant='outline'
              size='sm'
              onClick={handleDeleteClick}
              aria-label='Delete edge'
              title='Delete edge'
            >
              <Trash2 size={16} />
              Delete
            </Button>
          </div>
        </EdgeToolbar>
      )}
    </>
  );
}
